import { memo } from 'react';

import type { TranscriptRow } from './transcript-row-utils';
import type { AudioFileViewerSize } from '../audio-file-viewer';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

export type TranscriptRowPhase = 'played' | 'active' | 'unplayed';

const getWordColour = (isPlaying: boolean, phase: TranscriptRowPhase, isPlayed: boolean) => {
  if (!isPlaying) return 'text-gray-800';
  if (phase === 'played') return 'text-gray-900';
  if (phase === 'unplayed') return 'text-gray-950/40';
  return isPlayed ? 'text-gray-900' : 'text-gray-950/40';
};

interface TranscriptRowItemProps {
  row: TranscriptRow;
  index: number;
  rowCount: number;
  highlightIds: string[] | null;
  isHighlightedFact: boolean;
  isHighlightedBlock: boolean;
  isPlaying: boolean;
  phase: TranscriptRowPhase;
  /** Only consulted while `phase` is `active`, so it can't bust a non-active row's memo. */
  playedWordCount: number;
  onActivate: (row: TranscriptRow, highlightIds: string[] | null, isHighlightedFact: boolean) => void;
  onAssignToNewSpeaker?: (blockId: string) => void;
  isAssigningToNewSpeaker?: boolean;
  isNewSpeakerAssignmentDisabled?: boolean;
  size?: AudioFileViewerSize;
}

export const TranscriptRowItem = memo(function TranscriptRowItem({
  row,
  index,
  rowCount,
  highlightIds,
  isHighlightedFact,
  isHighlightedBlock,
  isPlaying,
  phase,
  playedWordCount,
  onActivate,
  onAssignToNewSpeaker,
  isAssigningToNewSpeaker = false,
  isNewSpeakerAssignmentDisabled = false,
  size = 'default',
}: TranscriptRowItemProps) {
  const isHighlighted = highlightIds !== null || isHighlightedBlock;
  const isFocused = isHighlightedFact || isHighlightedBlock;
  const handleClick = () => {
    // A drag that selected text still ends in a click, which would otherwise seek out from under it.
    if (!document.getSelection()?.isCollapsed) return;
    onActivate(row, highlightIds, isHighlightedFact);
  };

  return (
    <div
      role="listitem"
      aria-posinset={index + 1}
      aria-setsize={rowCount}
      data-row-index={index}
      className={cn('group/row relative', row.showsSpeakerGap && 'pt-3')}
    >
      <button
        type="button"
        // Lets the transcript restore focus if auto-scroll unmounts this row and it comes back.
        data-block-id={row.blockId}
        onClick={handleClick}
        title={`Play from ${row.timeLabel}`}
        className={cn(
          '-mx-3 grid w-[calc(100%+1.5rem)] cursor-text grid-cols-[6rem_3.25rem_minmax(0,1fr)] gap-4 rounded-md px-2.5 py-1 text-left transition-colors select-text hover:bg-gray-50 focus:outline-none',
          isHighlighted &&
            (isFocused ? 'bg-teal-400/20 hover:bg-teal-400/25' : 'bg-yellow-200/30 hover:bg-yellow-200/40'),
        )}
      >
        <span
          data-transcript-speaker
          className={cn(
            'text-right font-sans text-gray-900',
            size === 'sm' ? 'text-xs leading-7' : 'text-sm leading-loose',
          )}
        >
          {row.speakerLabel}
        </span>

        <span
          className={cn(
            'text-right font-sans text-xs text-gray-400 tabular-nums',
            size === 'sm' ? '-translate-y-0.5 text-[11px] leading-7' : 'pt-px leading-7',
          )}
        >
          {row.timeLabel}
        </span>

        {/* The insets are load-bearing: `readColumnWidths` measures this span and
            nets off its padding to model line breaks. */}
        <span
          data-transcript-text
          className={cn(
            '-mx-1.5 px-2 font-sans duration-150',
            size === 'sm' ? 'text-xs leading-7' : 'text-sm leading-loose',
          )}
        >
          {row.hasLowConfidence && (
            <span className="flex items-center gap-1.75 text-xs leading-7 font-normal whitespace-nowrap text-gray-500">
              <Icon className="lucide--circle-alert text-red-500" />
              Low transcription confidence. Please verify.
            </span>
          )}

          {row.wordTexts.map((word, wordIndex) => (
            <span
              key={wordIndex}
              className={cn(
                'transition-colors duration-200',
                getWordColour(isPlaying, phase, wordIndex < playedWordCount),
              )}
            >
              {wordIndex === 0 ? word : ` ${word}`}
            </span>
          ))}
        </span>
      </button>

      {onAssignToNewSpeaker && (
        <Button
          type="button"
          variant="white"
          size="sm"
          disabled={isNewSpeakerAssignmentDisabled}
          className="absolute -right-1.5 bottom-1.5 z-10 whitespace-nowrap opacity-0 shadow-lg/4 ring-offset-transparent transition-opacity duration-150 group-focus-within/row:opacity-100 group-hover/row:opacity-100 focus-within:ring-offset-white disabled:cursor-wait disabled:text-gray-400 disabled:opacity-100 disabled:[&_.iconify]:text-gray-400"
          onClick={() => onAssignToNewSpeaker(row.blockId)}
        >
          <Icon className={isAssigningToNewSpeaker ? 'lucide--loader animate-spin' : 'lucide--plus'} />
          Create new speaker
        </Button>
      )}
    </div>
  );
});
